import { motion } from "framer-motion";
import QuoteModal from "@/components/QuoteModal";
import brandMercier from "@/assets/mercier-yellow-birch-suede-elegancia.webp";
import brandLauzon from "@/assets/lauzon-collection-designer-flooring.webp";
import brandViking from "@/assets/viking-ml-heath.webp";
import brandIndusparquet from "@/assets/brand-indusparquet.webp";
import brandOldeWood from "@/assets/brand-olde-wood.webp";
import brandCali from "@/assets/brand-cali-floors.webp";
import brandQuickStep from "@/assets/brand-quickstep.webp";
import brandBlueForest from "@/assets/brand-blue-forest.webp";
import brandArmstrong from "@/assets/brand-armstrong-alterna.webp";
import brandMichiganPlank from "@/assets/brand-michigan-plank.webp";
import brandDreamweaver from "@/assets/brand-dreamweaver.webp";
import brandBellaCera from "@/assets/brand-bella-cera-villa-borghese.webp";

const allBrands = [
  { title: "Mercier", sub: "Premium Solid & Engineered Collections", img: brandMercier, alt: "Mercier Elegancia yellow birch floor in Suede", href: "/brands/mercier", span: "md:col-span-2 md:row-span-2" },
  { title: "Lauzon", sub: "The Expert Series: Solid & Engineered", img: brandLauzon, alt: "Lauzon designer hardwood floor", href: "/brands/lauzon", span: "md:col-span-2" },
  { title: "Viking", sub: "European Engineered Wide Plank White Oak", img: brandViking, alt: "Viking Modern Loft brushed European white oak engineered floor in Heath", href: "/brands/viking/", span: "md:col-span-2" },
  { title: "Indusparquet", sub: "Rare Species & High Janka Durability", img: brandIndusparquet, alt: "Indusparquet Solido solid Brazilian chestnut floor, wire brushed, in Whiskey Barrel", href: "/brands/indusparquet/", span: "" },
  { title: "Cali Floors", sub: "High Performance Luxury Vinyl (LVP)", img: brandCali, alt: "Cali flooring in a Michigan home", href: "/brands/cali", span: "" },
  { title: "Olde Wood Limited", sub: "Authentic Reclaimed Hardwood & Beams", img: brandOldeWood, alt: "Olde Wood reclaimed wide plank floor", href: "/brands/olde-wood", span: "" },
  { title: "Quick-Step", sub: "Waterproof Laminate with Lifetime Warranty", img: brandQuickStep, alt: "Quick Step waterproof laminate floor", href: "/brands/quick-step", span: "" },
  { title: "Bella Cera", sub: "Villa Borghese II  Double-Smoked French Oak", img: brandBellaCera, alt: "Bella Cera Villa Borghese II double smoked French oak floor", href: "/brands/bella-cera", span: "md:col-span-2" },
  { title: "Bamboo Flooring", sub: "High Density Strand Woven Bamboo & Eucalyptus", img: brandBlueForest, alt: "Strand woven bamboo floor", href: "/brands/ironwood", span: "md:col-span-2" },
  { title: "Armstrong Alterna", sub: "Luxury Vinyl Tile (LVT)", img: brandArmstrong, alt: "Armstrong Alterna engineered tile floor", href: "/brands/alterna", span: "" },
  { title: "Michigan Plank Flooring", sub: "Custom Wide-Plank European White Oak", img: brandMichiganPlank, alt: "Michigan Plank hickory floor below a staircase with iron balusters, Northville, MI", href: "/michigan-plank-flooring", span: "md:col-span-2" },
  { title: "Dreamweaver Carpet", sub: "Premium Stain Resistant Carpet & Stair Runners", img: brandDreamweaver, alt: "Dreamweaver stain resistant carpet", href: "/dreamweaver-carpet", span: "" },
];

const ProductCatalog = () => (
  <section className="bg-background py-14 md:py-20 lg:py-28" id="products">
    <div className="max-w-[1200px] mx-auto px-5 md:px-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 md:mb-10"
      >
        <div>
          <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-3">
            Premium Hardwood Flooring Catalog
          </p>
          <h2 className="font-display text-[clamp(28px,5vw,52px)] font-semibold text-foreground leading-[1.1]">
            Inspector Approved Brands for{" "}
            <em className="italic font-normal text-primary">Michigan Homes.</em>
          </h2>
          <p className="mt-3 md:mt-4 text-[13px] md:text-[15px] font-light text-muted-foreground leading-relaxed max-w-xl">
            We don't carry everything  we carry the right things. After decades in the trade, including years in manufacturing, we've curated a portfolio of brands we'd proudly install in our own homes.
          </p>
        </div>
        <QuoteModal
          trigger={
            <button className="mt-4 md:mt-0 inline-flex items-center gap-2 border border-border text-foreground px-6 py-3 rounded-full text-xs font-medium hover:border-primary/30 hover:shadow-md transition-all self-start md:self-auto">
              View Full Catalog →
            </button>
          }
        />
      </motion.div>

      {/* Seamless Mosaic */}
      <div className="rounded-[28px] overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-[4px]" style={{ gridAutoFlow: "dense", gridAutoRows: "minmax(220px, 1fr)" }}>
          {allBrands.map((brand, i) => {
            const isLarge = brand.span.includes("md:col-span-2") && brand.span.includes("md:row-span-2");
            const isTall = !isLarge && brand.span.includes("md:row-span-2");
            return (
              <motion.a
                key={brand.title}
                href={brand.href}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className={`group relative overflow-hidden block aspect-[4/3] md:aspect-auto min-h-[220px] md:min-h-0 ${brand.span} ${isLarge ? "md:aspect-square" : isTall ? "md:aspect-auto" : ""}`}
              >
                <img width={800} height={534} loading="lazy"
                  src={brand.img}
                  alt={brand.alt}
                  className="absolute inset-0 w-full h-full object-cover grayscale-[20%] contrast-[1.1] group-hover:grayscale-0 group-hover:scale-[1.05] transition-all duration-[550ms] ease-[cubic-bezier(.25,.46,.45,.94)]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <div className={`absolute bottom-0 left-0 ${isLarge ? "p-5 md:p-7" : "p-4 md:p-5"}`}>
                  <h3 className={`font-display font-semibold text-primary-foreground leading-tight ${isLarge ? "text-lg md:text-[26px]" : "text-base md:text-base"}`}>
                    {brand.title}
                  </h3>
                  <p className={`font-light text-primary-foreground/70 mt-0.5 ${isLarge ? "text-sm md:text-sm" : "text-[14px] md:text-[11px]"}`}>
                    {brand.sub}
                  </p>
                  <span className={`inline-block mt-2 border border-primary-foreground/25 text-primary-foreground/70 rounded-full font-medium opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 ${isLarge ? "px-4 py-1.5 text-[14px] md:text-xs" : "px-3 py-1 text-[13px] md:text-[11px]"}`}>
                    Explore {brand.title.split(":")[0]} →
                  </span>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

export default ProductCatalog;
